import { faCaravanSimple } from '@fortawesome/pro-solid-svg-icons';
import TripPriceClass from '../TripPriceClass';
import TrainBillHandler from './TrainBillHandler';
import {
    type PassengerCount,
    type PassengerFare,
    type OptionalService,
    type FreeService,
    PrivacyItems,
} from '@itinerary/types';
import { BuyExtraFields, PassengerFields } from '@store/form';
import { type InvoiceEntry } from '@store/invoice';
import { PaxType } from '@utils/types';
import {
    PricesApiApiGetPricingGet,
    CreateReservesApiApiReserveSeatGet,
    type TrainClassResponse,
    type WagonServiceDto,
    type ReserveSeatResult,
} from '@clients/TrainApi';
import type PriceClassExtraOption from '@itinerary/PriceClassExtraOption';
import { PaxTypeTitles } from '@itinerary/trip/TripModuleTypes';
import VerifyResult from '@itinerary/VerifyResult';
import PassengerForm from '@itinerary/PassengerForm';
import {
    ItineraryVerifyException,
    VerificationStatus,
} from '@itinerary/Exceptions';
import { type SellType, SellTypeCode } from '@utils/train';

export default class TrainPriceClass extends TripPriceClass {
    hasSpecialDiscount = false;
    isForRoundTrip = false;

    OwnerName: string | null = null;

    ownerID: number | null = null;
    private _netPrice = 0;

    override totalPayableForCheck: number | null = null;

    override totalPriceForCheck: number | null = null;

    override capacity = 0;

    override showCapacity = true;

    override capacityText = '';

    override creditPrivacy = PrivacyItems.None;
    override giftCardPrivacy = PrivacyItems.None;

    override loadableTermsUrl: string | null = null;

    _passengerFares: PassengerFare[] = [];

    wagonName = '';

    private _discount = 0;

    airConditioning = false;

    media = false;

    isCompartment = false;

    compartmentCapacity = 0;

    wagonID = 0;

    hasHotel = false;

    isAvailable = false;

    override isReservable = true;

    override cancellationTerms: string | null = '';

    id = 0;

    override title = '';

    override descriptionItems: string[] = [];

    priceId: string | null = null;
    available = true;

    wagonService: WagonServiceDto[] = [];

    getExclusiveNeedCapacity(totalCount: number): number {
        return (
            Math.ceil(totalCount / this.compartmentCapacity) *
            this.compartmentCapacity
        );
    }

    /**
     * Returns the extra options that may be applied to the price class.
     * Here, options are exclusive compartment, when applicable
     */
    override getAvailableExtraOptions(
        count: PassengerCount
    ): PriceClassExtraOption[] {
        if (this.isCompartment) {
            const needCompartmentCapacity = this.getExclusiveNeedCapacity(
                count.adultCount + count.childCount + count.adultCount
            );
            const disabled = this.capacity < needCompartmentCapacity;
            return [
                {
                    id: 'exclusive',
                    title: 'کوپه دربست',
                    description: disabled
                        ? 'ظرفیت قطار برای کوپه دربست بر‌اساس تعداد مسافرین شما ناکافی است.'
                        : 'اختصاص یک کوپه دربست با پرداخت هزینه صندلی‌های خالی باقی‌مانده',
                    price: 0,
                    disabled,
                    icon: faCaravanSimple,
                },
            ];
        } else {
            return [];
        }
    }

    override get prePriceDiscountPercent() {
        return toValue(this.servicesPreDiscountPercent).Train;
    }

    get isExclusive(): boolean {
        return this.selectedExtraOptions.some(o => o.id === 'exclusive');
    }

    get singlePrice() {
        return (this.singlePayable ?? 0) + (this.discount ?? 0);
    }

    get styledWagonName(): string {
        let styledWagonName = this.wagonName;
        if (styledWagonName.charAt(0) === '(') {
            styledWagonName = styledWagonName.slice(
                1,
                styledWagonName.length - 1
            );
        }
        if (styledWagonName.charAt(styledWagonName.length - 1) === ')') {
            styledWagonName = styledWagonName.slice(0, styledWagonName.length);
        }
        styledWagonName = styledWagonName.replaceAll('(', '-');
        styledWagonName = styledWagonName.replaceAll(')', '');
        return styledWagonName;
    }

    private getEmptySeatCount(count: PassengerCount): number {
        return (
            this.compartmentCapacity -
            ((count.adultCount +
                count.childCount +
                (count.infantSeatCount ?? 0) +
                (count.foreignerCount ?? 0) -
                1) %
                this.compartmentCapacity) -
            1
        );
    }

    override get netPrice() {
        return this.haveCustomDiscount
            ? (this._netPrice ?? 0) - (this._discount ?? 0)
            : this._netPrice ?? 0;
    }

    get discount() {
        return this.haveCustomDiscount
            ? this.getPreDiscountAmount(
                  (this._netPrice ?? 0) - (this._discount ?? 0)
              )
            : this._discount ?? 0;
    }

    override get passengerFares() {
        return this._passengerFares.map(fare => {
            const netCustomDiscountAmount =
                (fare.price ?? 0) - (fare.discount ?? 0);
            return {
                ...fare,
                price: this.haveCustomDiscount
                    ? this.getPriceWithPreDiscount(netCustomDiscountAmount)
                    : fare.price ?? 0,
                discount: this.haveCustomDiscount
                    ? this.getPreDiscountAmount(netCustomDiscountAmount)
                    : fare.discount ?? 0,
            };
        });
    }

    constructor(
        public price?: TrainClassResponse,
        public international?: boolean,
        public sellType?: SellType,
        exclusive?: boolean,
        totalCount = 1
    ) {
        super();

        if (price == null) {
            return;
        }
        this._netPrice = price.price ?? 0;
        this._discount = price.discount ?? 0;
        this.capacity = price.capacity ?? 0;
        this.capacityText = price.capacityString ?? '';
        if ((price.minPersons ?? 0) > totalCount) {
            this.isReservable = false;
        }
        this._passengerFares = [
            {
                paxType: PaxType.Adult,
                price: price.price ?? 0,
                discount: price.discount ?? 0,
            },
        ];

        this.isForRoundTrip = !!price.roundTrip;
        this.ownerID = price.owner ?? 0;
        this.OwnerName = price.ownerName ?? '';
        this.wagonName = price.wagonName ?? '';
        this.airConditioning = !!price.airConditioning;
        this.media = !!price.media;
        this.isCompartment = !!price.isCompartment;
        this.compartmentCapacity = price.compartmentCapacity ?? 0;
        this.wagonID = price.wagonID ?? 0;
        this.hasHotel = !!price.hasHotel;
        this.isAvailable = !!price.isAvailable;
        this.cancellationTerms = price.cancellationTerms ?? '';
        this.id = price.id ?? 0;
        this.title = price.wagonName ?? '';
        this.descriptionItems = [
            `واگن ${this.isCompartment ? 'کوپه‌ای' : 'سالنی'}${
                this.isCompartment ? ` ${this.compartmentCapacity} نفره` : ''
            }`,
        ];
        this.available = !!price.reservationAvailable;
        this.hasSpecialDiscount = !!price.hasSpecialDiscount;
        this.wagonService = price.wagonServices ?? [];
        if (exclusive) {
            this.selectedExtraOptions = this.getAvailableExtraOptions({
                adultCount: 1,
                childCount: 0,
                infantCount: 0,
            });
        }
    }

    private getVerifiedPrice(price: number, discount: number) {
        return this.haveCustomDiscount ? price : price + discount;
    }

    private getVerifiedDiscount(price: number, discount: number) {
        return this.haveCustomDiscount
            ? this.getPreDiscountAmount(price)
            : discount;
    }

    createPassengerForms(reserveData: ReserveSeatResult) {
        let defaultFields =
            PassengerFields.Gender |
            PassengerFields.Nationality |
            PassengerFields.BirthDate;
        let buyExtraFields = BuyExtraFields.None;

        const iranianExtraFields =
            PassengerFields.NationalCode |
            PassengerFields.PersianFirstName |
            PassengerFields.PersianLastName;
        const foreignerExtraFields =
            PassengerFields.NationalityCode |
            PassengerFields.PassportExpiry |
            PassengerFields.PassportNumber |
            PassengerFields.FirstName |
            PassengerFields.LastName;

        if (this.international) {
            defaultFields |=
                PassengerFields.FirstName |
                PassengerFields.LastName |
                PassengerFields.PersianFirstName |
                PassengerFields.PersianLastName;
        }

        if (this.sellType?.code === 4) {
            buyExtraFields |= BuyExtraFields.CarModel | BuyExtraFields.CarPlate;
        }

        if (reserveData?.optionalServices?.length) {
            buyExtraFields |= BuyExtraFields.PackageOptionalService;
        }

        if (reserveData?.freeServices?.length) {
            buyExtraFields |= BuyExtraFields.PackageFreeService;
        }

        const optionalServiceList: OptionalService[] = reserveData
            ?.optionalServices?.length
            ? [
                  {
                      title: 'انتخاب خدمات قطار',
                      services:
                          reserveData.optionalServices?.map(s => ({
                              id: `${s.id}`,
                              label: s.name?.replace(/ي/g, 'ی') ?? '',
                              price: s.price ?? 0,
                          })) ?? [],
                  },
              ]
            : [];

        const freeServiceList: FreeService[] = reserveData?.freeServices?.length
            ? [
                  {
                      title: 'خدمات رایگان قطار',
                      services:
                          reserveData.freeServices?.map(s => ({
                              id: s.code ?? 0,
                              label: s.name?.replace(/ي/g, 'ی') ?? '',
                              price: 0,
                          })) ?? [],
                  },
              ]
            : [];

        optionalServiceList?.forEach(s => {
            if (s.services.length) {
                [s.selectedService] = s.services;
            }
        });

        const foreignerMessages = [
            'مبلغ بلیط برای مسافرین خارجی می‌تواند متفاوت باشد.',
        ];

        const adultForm: PassengerForm = new PassengerForm({
            title: PaxTypeTitles[PaxType.Adult],
            type: PaxType.Adult,
            defaultFields,
            iranianExtraFields,
            foreignerExtraFields,
            buyExtraFields,
            optionalServiceList,
            freeServiceList,
            foreignerMessages,
        });

        const childForm: PassengerForm = new PassengerForm({
            title: PaxTypeTitles[PaxType.Child],
            type: PaxType.Child,
            defaultFields,
            iranianExtraFields,
            foreignerExtraFields,
            buyExtraFields,
            optionalServiceList,
            freeServiceList,
            foreignerMessages,
        });

        let infantBuyExtraFields =
            buyExtraFields &
            (BuyExtraFields.All ^ BuyExtraFields.PackageOptionalService);
        infantBuyExtraFields |= BuyExtraFields.InfantSeat;

        const infantForm: PassengerForm = new PassengerForm({
            title: PaxTypeTitles[PaxType.Infant],
            type: PaxType.Infant,
            defaultFields,
            iranianExtraFields,
            foreignerExtraFields,
            buyExtraFields: infantBuyExtraFields,
            optionalServiceList,
            freeServiceList,
            foreignerMessages,
        });

        return {
            adultForm,
            childForm,
            infantForm,
        };
    }

    override async verify(
        count: PassengerCount,
        billID: number | null
    ): Promise<VerifyResult> {
        const realCount =
            count.adultCount + count.childCount + count.infantCount;

        try {
            const { data } = await CreateReservesApiApiReserveSeatGet({
                trainID: this.priceId ? undefined : this.id,
                priceId: this.priceId || undefined,
                adultCount: count.adultCount,
                childCount: count.childCount,
                infantCount: count.infantCount,
                includeOptionalServices: true,
                exclusive: this.isExclusive,
                genderCode: this.sellType?.code || 3,
                seatCount: count.adultCount + count.childCount,
                billID: billID ?? undefined,
            });

            if (!data.result) {
                throw data;
            }

            const adultPrice = this.getVerifiedPrice(
                data.adultPrice?.price ?? 0,
                data.adultPrice?.discount ?? 0
            );
            const adultDiscount = this.getVerifiedDiscount(
                data.adultPrice?.price ?? 0,
                data.adultPrice?.discount ?? 0
            );

            const childPrice = this.getVerifiedPrice(
                data.childPrice?.price ?? 0,
                data.childPrice?.discount ?? 0
            );
            const childDiscount = this.getVerifiedDiscount(
                data.childPrice?.price ?? 0,
                data.childPrice?.discount ?? 0
            );

            const infantPrice = this.getVerifiedPrice(
                data.infantPrice?.price ?? 0,
                data.infantPrice?.discount ?? 0
            );
            const infantDiscount = this.getVerifiedDiscount(
                data.infantPrice?.price ?? 0,
                data.infantPrice?.discount ?? 0
            );

            const emptySeatPrice = this.getVerifiedPrice(
                data.emptySeatPrice?.price ?? 0,
                data.emptySeatPrice?.discount ?? 0
            );
            const emptySeatDiscount = this.getVerifiedDiscount(
                data.emptySeatPrice?.price ?? 0,
                data.emptySeatPrice?.discount ?? 0
            );

            const payable =
                (data.adultCount ?? 0) * adultPrice +
                (data.childCount ?? 0) * childPrice +
                (data.infantCount ?? 0) * infantPrice +
                (data.emptySeatCount ?? 0) * emptySeatPrice;
            const discount =
                (data.adultCount ?? 0) * adultDiscount +
                (data.childCount ?? 0) * childDiscount +
                (data.infantCount ?? 0) * infantDiscount +
                (data.emptySeatCount ?? 0) * emptySeatDiscount;

            const { adultForm, childForm, infantForm } =
                this.createPassengerForms(data);

            return new VerifyResult(new TrainBillHandler(), {
                billId: data.billID,
                billCode: data.billCode?.toString() ?? '',
                haveCustomDiscount: !!this.prePriceDiscountPercent,
                creditPrivacy: this.creditPrivacy,
                giftCardPrivacy: this.giftCardPrivacy,
                priceEntries: [
                    {
                        prices: [
                            {
                                paxType: PaxType.Adult,
                                count: data.adultCount ?? 0,
                                price: adultPrice,
                                title:
                                    this.sellType?.code === SellTypeCode.Car
                                        ? 'حمل خودرو'
                                        : undefined,
                                discount: adultDiscount,
                            },
                            {
                                paxType: PaxType.Child,
                                count: data.childCount ?? 0,
                                price: childPrice,
                                discount: childDiscount,
                            },
                            {
                                paxType: PaxType.Infant,
                                count: data.infantCount ?? 0,
                                price: infantPrice,
                                discount: infantDiscount,
                            },
                            {
                                paxType: PaxType.EmptySeat,
                                count: data.emptySeatCount ?? 0,
                                price: emptySeatPrice,
                                discount: emptySeatDiscount,
                            },
                        ],
                    },
                ],
                remainingTime: this.remainingTimeCalculator({
                    passengersCount: realCount,
                }),
                payablePrice: payable,
                discount,
                totalPrice: payable + discount,
                adultForm,
                childForm,
                infantForm,
                paxList: [
                    ...Array(count.adultCount)
                        .fill(0)
                        .map(_ => adultForm.clone()),

                    ...Array(count.childCount)
                        .fill(0)
                        .map(_ => childForm.clone()),

                    ...Array(count.infantCount)
                        .fill(0)
                        .map(_ => infantForm.clone()),
                ],
                needsDestinationMobile: false,
                maxCapacity:
                    this.sellType?.code === 4
                        ? 1
                        : Math.min(
                              data.maxCapacity ?? 0,
                              this.compartmentCapacity
                          ),
                warningMessages: [],
                reserveNotes: data.reservationMessage
                    ? [data.reservationMessage]
                    : [],
                isCaptchaEnabled: data.isCaptchaEnabled,
            });
        } catch (error) {
            if (error instanceof ItineraryVerifyException) {
                throw error;
            }
            throw new ItineraryVerifyException(
                VerificationStatus.UnknownError,
                null,
                error
            );
        }
    }

    private getPrePrice(price: number, discount: number) {
        return this.haveCustomDiscount
            ? this.getPriceWithPreDiscount(price - discount)
            : price;
    }

    private getPreDiscount(price: number, discount: number) {
        return this.haveCustomDiscount
            ? this.getPreDiscountAmount(price - discount)
            : discount ?? 0;
    }

    override async loadPreInvoice(
        count: PassengerCount
    ): Promise<InvoiceEntry[]> {
        const { data } = await PricesApiApiGetPricingGet({
            ...count,
            emptySeatCount: this.isExclusive
                ? this.getEmptySeatCount(count)
                : 0,
            infantWithSeatCount: count.infantSeatCount,
            capacityId: this.id,
        });

        return [
            ...(data.foreignerPrice
                ? [
                      {
                          paxType: PaxType.Adult,
                          price: this.getPrePrice(
                              data?.foreignerPrice?.price ?? 0,
                              data?.foreignerPrice?.discount ?? 0
                          ),
                          discount: this.getPreDiscount(
                              data?.foreignerPrice?.price ?? 0,
                              data?.foreignerPrice?.discount ?? 0
                          ),
                          count: count.foreignerCount ?? 0,
                          title: 'مسافر خارجی',
                      },
                  ]
                : []),
            ...(data.adultPrice
                ? [
                      {
                          paxType: PaxType.Adult,
                          price: this.getPrePrice(
                              data.adultPrice.price ?? 0,
                              data.adultPrice.discount ?? 0
                          ),
                          discount: this.getPreDiscount(
                              data.adultPrice.price ?? 0,
                              data.adultPrice.discount ?? 0
                          ),
                          count: count.adultCount,
                      },
                  ]
                : []),
            ...(data.childPrice
                ? [
                      {
                          paxType: PaxType.Child,
                          price: this.getPrePrice(
                              data.childPrice.price ?? 0,
                              data.childPrice.discount ?? 0
                          ),
                          discount: this.getPreDiscount(
                              data.childPrice.price ?? 0,
                              data.childPrice.discount ?? 0
                          ),
                          count: count.childCount,
                      },
                  ]
                : []),
            ...(data.infantPrice
                ? [
                      {
                          paxType: PaxType.Infant,
                          price: this.getPrePrice(
                              data.infantPrice.price ?? 0,
                              data.infantPrice.discount ?? 0
                          ),
                          discount: this.getPreDiscount(
                              data.infantPrice.price ?? 0,
                              data.infantPrice.discount ?? 0
                          ),
                          count: count.infantCount,
                      },
                  ]
                : []),
            ...(data.infantWithSeatPrice
                ? [
                      {
                          paxType: PaxType.Infant,
                          price: this.getPrePrice(
                              data.infantWithSeatPrice.price ?? 0,
                              data.infantWithSeatPrice.discount ?? 0
                          ),
                          discount: this.getPreDiscount(
                              data.infantWithSeatPrice.price ?? 0,
                              data.infantWithSeatPrice.discount ?? 0
                          ),
                          count: count.infantSeatCount ?? 0,
                          title: 'نوزاد با صندلی',
                      },
                  ]
                : []),
            ...(data.emptySeatPrice
                ? [
                      {
                          paxType: PaxType.EmptySeat,
                          price: this.getPrePrice(
                              data.emptySeatPrice.price ?? 0,
                              data.emptySeatPrice.discount ?? 0
                          ),
                          discount: this.getPreDiscount(
                              data.emptySeatPrice.price ?? 0,
                              data.emptySeatPrice.discount ?? 0
                          ),
                          count: this.getEmptySeatCount(count),
                      },
                  ]
                : []),
        ];
    }
}
